import { Button, Card, Col, DatePicker, Form, Input, Popover, Row, Select, Space, Switch, Tooltip } from 'antd';
import DisplayAlert from '../../../components/Commons/Alert';
import GenericTable from '../../../components/Commons/Table/generic-table.component';
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, FileTextOutlined, FilterOutlined } from "@ant-design/icons";
import ShowDataLength from '../../../components/Commons/Table/results-length-table.component';
import { useEffect, useState } from 'react';
import { getAll, remove, update } from '../../../services/bookings.service';
import { getAll as loadOperators } from '../../../services/workers.service';
import { PaginationProps } from 'antd/lib';
import { FilterValue } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import { AlertService } from '../../../services/alert.service';
import { useMutation, useQuery } from "@tanstack/react-query";
import ConfirmModal from '../../../components/Commons/Modal/confirm-modal.component';
import { cutLongText, downloadExcel, formatCurrency, formatCurrencyToExport } from '../../../services/utils';
import ProfilePermissionService from "../../../services/profilePermissions.service";
import moment from 'moment';
import { RangePickerProps } from 'antd/lib/date-picker';
import dayjs from 'dayjs';
import 'dayjs/locale/pt';
import ModalVoucher from './ModalVoucher';
import { isMobile } from 'react-device-detect';
import ExportBtn from '../../../components/Commons/Export/ExportButton';
import FilterButton from '../../../components/Commons/Table/FilterButton';
import SearchInput from '../../../components/Commons/Input/SearchInput';

const { Search } = Input;

export default function Bookings(props: any) {
    const [bookings, setBookings] = useState([]);
    const [bookingsToExport, setBookingsToExport] = useState([]);
    const [exportIsLoading, setExportIsLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number | undefined>(10);
    const [total, setTotal] = useState<number>(0);
    const [search, setSearch] = useState<string>('');
    const [order, setOrder] = useState<string>('created_at-desc');
    const [removeBooking, setRemoveBooking] = useState<any | undefined>(undefined);
    const [bookingVoucher, setBookingVoucher] = useState<any | undefined>(undefined);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [finished, setFinished] = useState<string>('');
    const [changeFinished, setChangeFinished] = useState<{ value: boolean, id: number } | undefined>(undefined);
    const [formSearch] = Form.useForm();
    const [formFilter] = Form.useForm();
    const [client, setClient] = useState<string>('');
    const [operatorId, setOperatorId] = useState<number | undefined>();
    const [operators, setOperators] = useState<[]>([]);

    const navigate = useNavigate();

    const profilePermissionService = ProfilePermissionService.getInstance();

    const { isLoading: bookingsLoading, refetch: loadBookings } = useQuery(
        ['loadBookings', currentPage, search, order, perPage, startDate, endDate, finished, client, operatorId],
        () => getAll({
            page: currentPage, per_page: perPage, order, firstService: '1',
            search, start_date: startDate, end_date: endDate, finished, 
            client, operator_id: operatorId
        }),
        {
            onSuccess: response => {
                setBookings(response.data.map((booking: any) => {
                    return {
                        key: booking.data.id,
                        ...booking.data,
                        ...booking.relationships
                    }
                }));

                setBookingsToExport([]);
                setCurrentPage(response.meta.current_page);
                setPerPage(response.meta.per_page);
                setTotal(response.meta.total);
            },
            refetchOnWindowFocus: false
        }
    );

    const { refetch: loadBookingsToExport } = useQuery(
        ['loadBookingsToExport'],
        () => getAll({ order, search, start_date: startDate, end_date: endDate, finished, client, operator_id: operatorId, firstService: '1' }),
        {
            onSuccess: response => setBookingsToExport(onMapDataToExport(response.data)),
            refetchOnWindowFocus: false,
            enabled: false
        }
    );
    
    useQuery(
        ['loadOperators'],
        () => loadOperators({ type: 'operators', status: 'active', with_relationship: '0' }),
        {
            onSuccess: response => setOperators(response.data.map((operator: any) => {
                return {
                    key: operator.data.id,
                    ...operator.data,
                    ...operator.relationships
                }
            })),
            refetchOnWindowFocus: false
        }
    );

    const { mutate: RemoveBookingMutate } = useMutation(remove,
        {
            onSuccess: () => {
                AlertService.sendAlert([
                    { text: `Reserva n° ${removeBooking?.id} apagada com sucesso.` }
                ]);

                setRemoveBooking(undefined);

                if (bookings.length === 1 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                } else {
                    loadBookings();
                }
            }
        }
    );

    const { mutate: updateBookingFinishedMutate } = useMutation(update, {
        onSuccess: () => loadBookings(),
        onError: () => loadBookings()
    });

    const onSearch = (search_value: any) => {
        setCurrentPage(1);
        setSearch(search_value);
    };

    const onFilter = () => {
        setCurrentPage(1);
        let start_date = formFilter.getFieldValue('filter_start_date');
        let end_date = formFilter.getFieldValue('filter_end_date');
        let clientValue = formFilter.getFieldValue('filter_client') || '';
        let operatorId = formFilter.getFieldValue('filter_operator_id') || '';

        setStartDate(start_date ? start_date.format('YYYY-MM-DD') : '');
        setEndDate(end_date ? end_date.format('YYYY-MM-DD') : '');
        setFinished(formFilter.getFieldValue('filter_finished'));
        setClient(clientValue);
        setOperatorId(operatorId ? Number.parseInt(operatorId, 10) : undefined);
    };

    const onClearFilters = () => {
        setCurrentPage(1);
        setStartDate('');
        setEndDate('');
        setFinished('');
        setClient('');
        setOperatorId(undefined);
        formFilter.resetFields();
    };
    
    
    /**
     * Handles the filtering of the operators by name.
     */
    const onFilterOperators = (
        input: string, option?: { key: string, value: number, children: string }
    ) => (option?.children || '').toLowerCase().includes(input.toLowerCase());

    const handleTableChange = (
        pagination: PaginationProps,
        filters: Record<string, FilterValue | null>,
        sorter: any
    ) => {
        if (pagination.current != currentPage) {
            setCurrentPage(Number(pagination.current));
        }

        if (pagination.pageSize != perPage) {
            setPerPage(pagination.pageSize);
            setCurrentPage(1);
        }

        if (sorter.order) {
            let orderBy = `${sorter.field}-${sorter.order.substr(0, sorter.order.length - 3)}`;

            if (order != orderBy) {
                setOrder(orderBy);
            }
        }
    };

    const toPage = (page: string, booking_id: number = 0) => {
        let pages = {
            create: '/bookings/bookings/create',
            edit: `/bookings/bookings/${booking_id}/edit`
        };

        navigate(pages[page as keyof typeof pages]);
    };

    const disabledStartDate: RangePickerProps['disabledDate'] = (current) => {
        let endDate = formFilter.getFieldValue('filter_end_date');

        if (endDate) {
            return current >= endDate;
        }

        return false;
    };

    const disabledEndDate: RangePickerProps['disabledDate'] = (current) => {
        let startDate = formFilter.getFieldValue('filter_start_date');

        if (startDate) {
            return current <= startDate;
        }

        return false;
    };

    const onMapDataToExport = (data: any) => {
        return data.map((record: any) => {
            return {
                'ID Reserva': record?.data?.id,
                'Data da Reserva': `${moment(record?.relationships?.serviceVouchers[0]?.voucher?.start, 'YYYY-MM-DD')
                    .format('DD/MM/YYYY')}${record?.relationships?.serviceVouchers[0]?.voucher?.hour
                        ? moment(record?.relationships?.serviceVouchers[0]?.voucher?.hour, 'H:mm:ss').format('[, às] HH[h]mm')
                        : ''}`,
                'Cliente': record?.data?.client_name,
                'Cliente email': record?.data?.client_email,
                'Cliente telefone': record?.data?.client_phone,
                'Operador': record?.relationships?.operator?.data?.name,
                'Referência': record?.data?.reference,
                'Valor reserva': formatCurrencyToExport(record?.data?.value || 0),
                'Depósitos': formatCurrencyToExport(record?.data?.deposits_paid || 0),
                'Pax Grupo': record?.data?.pax_group,
                'Outras informações': record?.data?.additional_information,
            };
        });
    };

    const onChangeFinished = (checked: boolean, id: number) => {
        const obj = {
            values: { 'finished': checked }, id
        };

        updateBookingFinishedMutate(obj);
    };

    const exportData = () => downloadExcel(bookingsToExport, 'Reservas');

    const columns: any = [
        {
            title: isMobile ? 'ID' : 'ID Reserva', dataIndex: 'id', key: 'id', sorter: true,
            render: (_: any, record: any) => (<Space>
                {record.id}
                {record.emphasis && <Tooltip placement="top" title="Ação necessária">
                    <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                </Tooltip>}
            </Space>),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Data da Reserva', dataIndex: 'created_at', key: 'created_at', sorter: true,
            // render: (_: any, record: any) =>
            //     `${moment(record.start_date, 'YYYY-MM-DD').format('DD/MM/YYYY')}${record.hour ?
            //         moment(record.hour, 'H:mm:ss').format('[, às] HH[h]mm') :
            //         ''}`,
            render: (_: any, record: any) => {
                if (record?.serviceVouchers[0]?.voucher?.start) {
                    return `${moment(record?.serviceVouchers[0]?.voucher?.start, 'YYYY-MM-DD')
                        .format('DD/MM/YYYY')}${record?.serviceVouchers[0]?.voucher?.hour
                            ? moment(record?.serviceVouchers[0]?.voucher?.hour, 'H:mm:ss').format('[, às] HH[h]mm')
                            : ''}`;
                }

                return '';
            },
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Cliente', dataIndex: 'client_name', key: 'client_name', sorter: false,
            render: (_: any, record: any) => (cutLongText(record?.client_name || '', 40)),
            responsive: ['lg', 'xl'],
        },
        {
            title: 'Operador', dataIndex: 'operator', key: 'operator', sorter: false,
            render: (_: any, record: any) => (cutLongText(record?.operator?.data?.name || '', 40)),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Referência', dataIndex: 'reference', key: 'reference', sorter: false,
            responsive: ['lg', 'xl'],
        },
        {
            title: 'Valor reserva', dataIndex: 'value', key: 'value', sorter: true,
            render: (_: any, record: any) => {
                return formatCurrency(record?.value || 0)                
            },
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Finalizar', dataIndex: 'finished', key: 'finished', sorter: true, width: 150,
            render: (_: any, record: any) => {
                return (<Tooltip title={record.finished ? 'Marcar como não finalizada' : 'Marcar como finalizada'}>
                    <Space direction={isMobile ? 'vertical' : 'horizontal'}>
                        <Switch checked={record.finished}
                            style={{ marginRight: 10 }}
                            onChange={(event: boolean) => {
                                setChangeFinished({ value: event, id: record.id });

                                record.finished = event;
                            }} />
                        {record.finished ? 'Finalizada' : 'Aberta'}
                    </Space>
                </Tooltip>);
            }
        },
        {
            title: 'Ações', key: 'action', width: isMobile ? 60 : 150,
            render: (_: any, record: any) => (
                <GenericTable.Actions items={[
                    {
                        label: 'Editar',
                        icon: <EditOutlined />,
                        onClick: () => toPage('edit', record.id),
                    },
                    {
                        label: 'Apagar',
                        icon: <DeleteOutlined />,
                        onClick: () => setRemoveBooking(record),
                    },
                    {
                        label: 'Schedule',
                        icon: <FileTextOutlined />,
                        onClick: () => setBookingVoucher(record?.id),
                    }
                ]} enabled={
                    profilePermissionService.hasPermission('bookings-bookings:write')
                } />
            )
        }
    ];

    const filters = (
        <Form
            form={formFilter}
            name="filter_bookings"
            layout="vertical"
            className="form_filter_horizontal"
            autoComplete="off"
            style={isMobile ? {} : { width: 300 }}>
            <Row gutter={20}>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Data início" name="filter_start_date" className="item_filter">
                        <DatePicker size="large" placeholder="dd/mm/aaaa" showToday={false}
                            format={'DD/MM/YYYY'} disabledDate={disabledStartDate} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Data fim" name="filter_end_date" className="item_filter">
                        <DatePicker size="large" placeholder="dd/mm/aaaa" showToday={false}
                            format={'DD/MM/YYYY'} disabledDate={disabledEndDate} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Cliente" name="filter_client" className="item_filter">
                        <Input size="large" placeholder="Nome do cliente" />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Operador" name="filter_operator_id" className="item_filter">
                        <Select showSearch allowClear placeholder="Selecione um operador" size="large"
                            filterOption={onFilterOperators}>
                            {operators.map((operator: any) => (
                                <Select.Option key={operator.id} value={operator.id}>{operator.name}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Estado" name="filter_finished" className="item_filter">
                        <Select placeholder="Todos" value={finished} style={isMobile ? {} : { width: '100%' }} size="large"
                            options={[                                
                                { value: '', label: 'Todos' },
                                { value: '1', label: 'Finalizada' },
                                { value: '0', label: 'Não finalizada' },
                            ]} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Button onClick={onFilter} size="large">Filtrar</Button>

                    {(startDate || endDate || finished)
                        && <Button type="link" onClick={onClearFilters} size="large">Limpar</Button>
                    }
                </Col>
            </Row>
        </Form>
    );

    useEffect(() => {
        formFilter.setFieldsValue({
            filter_start_date: '',
            filter_end_date: '',
            filter_finished: '0'
        });
    }, []);

    useEffect(() => {
        if (!startDate) {
            formFilter.setFieldValue('filter_start_date', '');
        }

        if (!endDate) {
            formFilter.setFieldValue('filter_end_date', '');
        }

        formFilter.setFieldValue('filter_finished', finished);
    }, [startDate, endDate, finished]);

    useEffect(() => {
        if (bookingsToExport.length) {
            exportData();
        }

        setExportIsLoading(false);
    }, [bookingsToExport]);

    useEffect(() => {
        if (changeFinished) {
            onChangeFinished(changeFinished.value, changeFinished.id);
        }
    }, [changeFinished]);

    return (
        <>
            {!bookingVoucher && <DisplayAlert />}
            <Card bodyStyle={{ padding: 8 }}>
                <div>
                    <Row className="search_bar">
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                            <SearchInput
                                form={formSearch}
                                name="search_bookings"
                                buttonText="Pesquisar"
                                placeholder="Pesquisa por nome do cliente, nº reserva ou referência"
                                onSearch={onSearch}
                            />
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 6 }} xl={{ span: 6 }}
                            style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {profilePermissionService.hasPermission("bookings-bookings:write") &&
                                <Button type="primary" onClick={() => toPage('create')} size="large"
                                    style={isMobile ? {
                                        marginTop: 5, marginBottom: -30
                                    } : {}}
                                    block={isMobile}>Nova reserva</Button>
                            }
                        </Col>
                    </Row>
                </div>

                <div className="table">
                    <GenericTable
                        columns={columns}
                        dataSource={bookings}
                        pagination={{
                            current: currentPage,
                            pageSize: perPage,
                            showSizeChanger: true,
                            total: total
                        }}
                        loading={bookingsLoading}
                        totalChildren={
                            <div className="" style={isMobile ? {} : { marginTop: 8 }}>
                                <Row>
                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12 }}
                                        style={isMobile ? { marginBottom: 10 } : {}}>
                                        <ShowDataLength size={total} className="show_result_size" />
                                    </Col>

                                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12 }}
                                        style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Space>
                                            <FilterButton filters={filters} />

                                            <ExportBtn disabled={!total} loading={exportIsLoading} block={isMobile}
                                                onClick={() => {
                                                    if (bookingsToExport.length) {
                                                        exportData();
                                                    } else {
                                                        setExportIsLoading(true);
                                                        loadBookingsToExport();
                                                    }
                                                }} />
                                        </Space>
                                    </Col>
                                </Row>
                            </div>
                        }
                        empty={{
                            createTheFirst: !bookings.length && !search && !startDate && !endDate,
                            textOne: 'Nenhuma reserva criada.',
                            textTwo: '',
                            link: profilePermissionService.hasPermission("bookings-bookings:write")
                                ? '/bookings/bookings/create'
                                : '',
                            createText: 'Nova reserva'
                        }}
                        handleTableChange={handleTableChange}
                    />

                    <ConfirmModal
                        open={!!removeBooking}
                        title={`A reserva n° ${removeBooking?.id} tem serviços associados.`}
                        content='Se apagar esta reserva, todos os serviços associados serão apagados.'
                        okText="Apagar" cancelText="Voltar"
                        onOk={() => RemoveBookingMutate(removeBooking?.id)}
                        onCancel={() => setRemoveBooking(undefined)}
                    ></ConfirmModal>

                    <ModalVoucher bookingId={bookingVoucher} lang='en' onClose={() => setBookingVoucher(undefined)} />
                </div>
            </Card>
        </>
    );
}